import React, { useEffect, useMemo, useState } from "react";
import {
  CircularProgress,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import endpoints from "../../helpers/endpoints";
import fetch from "../../utils/fetch";
import { getAdminHeaders } from "../../utils/adminAuth";
import styles from "./AdminAccountManagement.module.scss";

type AdminAccount = {
  id: string;
  email: string;
  role: string;
  roleDetail?: {
    title?: string;
    name?: string;
  };
  userDetail?: {
    _id?: string;
    firstName?: string;
    lastName?: string;
    phone?: string;
    company?: string;
  };
  createdAt?: string;
};

type AdminRole = {
  id: string;
  name: string;
  title: string;
};

function formatDate(value?: string) {
  if (!value) return "-";
  return new Date(value).toLocaleString("vi-VN");
}

function normalizeEmails(value: string) {
  return Array.from(new Set(value.split(/[\s,;]+/).map((email) => email.trim().toLowerCase()).filter(Boolean)));
}

const fallbackRoles: AdminRole[] = [
  { id: "master", name: "master", title: "Admin cấp cao" },
  { id: "content-manager", name: "content-manager", title: "Content manager" },
];

function getResultArray(body: any) {
  if (Array.isArray(body?.result)) return body.result;
  if (Array.isArray(body?.result?.docs)) return body.result.docs;
  if (Array.isArray(body?.docs)) return body.docs;
  return null;
}

const AdminAccountManagement = () => {
  const [admins, setAdmins] = useState<AdminAccount[]>([]);
  const [roles, setRoles] = useState<AdminRole[]>(fallbackRoles);
  const [page, setPage] = useState(1);
  const [totalPage, setTotalPage] = useState(1);
  const [search, setSearch] = useState("");
  const [emails, setEmails] = useState("");
  const [role, setRole] = useState("master");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const query = useMemo(() => {
    const q: any = { page, limit: 20 };
    if (search.trim()) q.email = search.trim();
    return q;
  }, [page, search]);

  async function loadRoles() {
    const res: any = await fetch(endpoints.ADMINISTRATOR_ROLES, {
      query: { limit: 50 },
      headers: getAdminHeaders(),
    });
    const result = getResultArray(res?.body);
    if (res?.status < 400 && result && result.length > 0) {
      setRoles(result);
      if (!result.some((item: AdminRole) => item.name === role)) {
        setRole(result[0].name);
      }
    }
  }

  async function loadAdmins() {
    setLoading(true);
    setError("");
    try {
      const res: any = await fetch(endpoints.ADMINISTRATORS, {
        query,
        headers: getAdminHeaders(),
      });
      if (res?.status >= 400) {
        throw new Error(res?.body?.message || "Không tải được danh sách admin.");
      }
      const result = getResultArray(res.body);
      if (!result) {
        throw new Error("API admin trả dữ liệu không hợp lệ. Kiểm tra proxy /admin tới gateway.");
      }
      const meta = res.body?.meta || {};
      const total = meta.totalPage || meta.totalPages || meta.pages || Math.ceil((meta.total || 0) / 20) || 1;
      setAdmins(result);
      setTotalPage(Math.max(total, 1));
    } catch (err: any) {
      setError(err?.message || "Không tải được danh sách admin.");
    } finally {
      setLoading(false);
    }
  }

  async function addAdmins() {
    const emailList = normalizeEmails(emails);
    if (emailList.length === 0) {
      setError("Vui lòng nhập ít nhất một email.");
      return;
    }

    setSubmitting(true);
    setError("");
    setMessage("");
    try {
      const res: any = await fetch(endpoints.ADMINISTRATORS, {
        method: "POST",
        headers: getAdminHeaders(),
        body: JSON.stringify(emailList.map((email) => ({ email, role }))),
      });
      if (res?.status >= 400) {
        throw new Error(res?.body?.message || "Không thêm được admin.");
      }
      setEmails("");
      setMessage(`Đã thêm ${res.body?.result?.success || emailList.length} admin.`);
      setPage(1);
      await loadAdmins();
    } catch (err: any) {
      setError(err?.message || "Không thêm được admin.");
    } finally {
      setSubmitting(false);
    }
  }

  async function deleteAdmin(admin: AdminAccount) {
    const confirmed = window.confirm(`Xóa quyền admin của ${admin.email}?`);
    if (!confirmed) return;

    setError("");
    setMessage("");
    try {
      const res: any = await fetch(`${endpoints.ADMINISTRATORS}/${encodeURIComponent(admin.id)}`, {
        method: "DELETE",
        headers: getAdminHeaders(),
      });
      if (res?.status >= 400) {
        throw new Error(res?.body?.message || "Không xóa được admin.");
      }
      setMessage(`Đã xóa admin ${admin.email}.`);
      await loadAdmins();
    } catch (err: any) {
      setError(err?.message || "Không xóa được admin.");
    }
  }

  useEffect(() => {
    loadRoles().catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadAdmins();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  return (
    <div className={styles.ContentWrapper}>
      <div className={styles.Header}>
        <div>
          <h1 className={styles.Title}>Quản lý admin hệ thống</h1>
          <p className={styles.Subtitle}>Quản lý tài khoản có quyền truy cập trang quản trị.</p>
        </div>
        {loading && <CircularProgress size={28} />}
      </div>

      {error && <div className={styles.Error}>{error}</div>}
      {message && <div className={styles.Success}>{message}</div>}

      <div className={styles.Panel}>
        <h2 className={styles.PanelTitle}>Thêm admin</h2>
        <div className={styles.FormGrid}>
          <div className={styles.InputGroup}>
            <label>Email admin</label>
            <textarea
              value={emails}
              placeholder="admin@example.com, operator@example.com"
              onChange={(event) => setEmails(event.target.value)}
            />
          </div>
          <div className={styles.InputGroup}>
            <label>Quyền</label>
            <select value={role} onChange={(event) => setRole(event.target.value)}>
              {roles.map((item) => (
                <option key={item.name} value={item.name}>
                  {item.title || item.name}
                </option>
              ))}
            </select>
          </div>
          <button className={styles.PrimaryButton} disabled={submitting} onClick={addAdmins}>
            {submitting ? "Đang thêm..." : "Thêm admin"}
          </button>
        </div>
      </div>

      <div className={styles.Panel}>
        <div className={styles.Toolbar}>
          <h2 className={styles.PanelTitle}>Danh sách admin</h2>
          <div className={`${styles.InputGroup} ${styles.Search}`}>
            <input
              value={search}
              placeholder="Tìm theo email"
              onChange={(event) => {
                setPage(1);
                setSearch(event.target.value);
              }}
            />
          </div>
        </div>

        <div className={styles.TableWrap}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Email</TableCell>
                <TableCell>Họ tên</TableCell>
                <TableCell>Số điện thoại</TableCell>
                <TableCell>Quyền</TableCell>
                <TableCell>Ngày tạo</TableCell>
                <TableCell align="right">Thao tác</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {admins.map((admin) => {
                const userName = [admin.userDetail?.firstName, admin.userDetail?.lastName].filter(Boolean).join(" ");
                return (
                  <TableRow key={admin.id}>
                    <TableCell>{admin.email}</TableCell>
                    <TableCell>{userName || "-"}</TableCell>
                    <TableCell>{admin.userDetail?.phone || "-"}</TableCell>
                    <TableCell>
                      <span className={styles.RoleBadge}>{admin.roleDetail?.title || admin.role}</span>
                    </TableCell>
                    <TableCell>{formatDate(admin.createdAt)}</TableCell>
                    <TableCell align="right">
                      <button className={styles.DangerButton} onClick={() => deleteAdmin(admin)}>
                        Xóa
                      </button>
                    </TableCell>
                  </TableRow>
                );
              })}
              {admins.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className={styles.Muted}>
                    Chưa có dữ liệu.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
          <Pagination count={totalPage} page={page} onChange={(_, value) => setPage(value)} />
        </div>
      </div>
    </div>
  );
};

export default AdminAccountManagement;
